<template>
    <b-container fluid>
        <h1 class="text-left">
            {{ title }}
            <em
                v-show="record.unavailable"
                class="error fas fa-exclamation-triangle"
                :title="$t('owsNotAvailable')"
            >
            </em>
        </h1>
        <br />
        <b-form
            ref="owsForm"
            class="col-xs-9 col-lg-5 pb-5"
            name="owsForm"
            v-on:submit.prevent="$_onSubmit"
        >
            <div>
                <b-form-group label="OWS type">
                    <b-form-radio-group
                        id="radioContentType"
                        name="radioContentType"
                        :disabled="$_hasUri()"
                        v-model="OwsType"
                        :options="OwsTypeOptions"
                    >
                    </b-form-radio-group>
                </b-form-group>
            </div>
            <!-- OWS name -->
            <x-input
                :text="labelName"
                v-model="record.name"
                @input="$_onInputName"
                id="ows-name"
                name="ows-name"
                :invalidMessage="nameMessage"
                :state="!errorInUse"
                :placeholder="placeholders.name"
                :maxlength="50"
                :required="true"
                :autofocus="true"
                :readonly="record.readonly"
            >
            </x-input>
            <!-- OWS WMS url -->
            <div v-show="OwsType === 'WMSWFS'">
                <x-input
                    :text="labelWmsUrl"
                    v-model="record.uri_WMS"
                    @input="$_onInputWmsUri"
                    id="ows-wmsuri"
                    name="ows-wmsuri"
                    :invalidMessage="uriWmsMessage"
                    :state="!noUriDefined && !uriWmsInvalidWords"
                    :placeholder="placeholders.wmsUri"
                    :maxlength="200"
                    :required="false"
                    :readonly="record.readonly"
                >
                </x-input>
                <!-- OWS WFS url -->
                <x-input
                    :text="labelWfsUrl"
                    v-model="record.uri_WFS"
                    @input="$_onInputWfsUri"
                    id="ows-wfsuri"
                    name="ows-wfsuri"
                    :invalidMessage="uriWfsMessage"
                    :state="!noUriDefined && !uriWfsInvalidWords"
                    :placeholder="placeholders.wfsUri"
                    :maxlength="200"
                    :required="false"
                    :readonly="record.readonly"
                >
                </x-input>
                <!-- OWS WFS version -->
                <div
                    class="col-xs-4 col-md-4 col-lg-3 pb-3"
                    v-show="showWfsVersionSelector"
                >
                    <x-select
                        id="ows-wfsversion"
                        name="ows-wfsversion"
                        :text="labelWfsVersion"
                        @input="$_onInputWfsVersion"
                        :value="getWfsVersion"
                        :options="getWfsVersionOptions"
                        :showDefaultOption="false"
                    >
                    </x-select>
                </div>
                <!-- OWS WMTS url -->
                <x-input
                    :text="labelWmtsUrl"
                    v-model="record.uri_WMTS"
                    @input="$_onInputWmtsUri"
                    id="ows-wmtsuri"
                    name="ows-wmtsuri"
                    :invalidMessage="uriWmtsMessage"
                    :state="!noUriDefined && !uriWmtsInvalidWords"
                    :placeholder="placeholders.wmtsUri"
                    :maxlength="200"
                    :required="false"
                    :readonly="record.readonly"
                >
                </x-input>
            </div>
            <div v-show="OwsType === 'OGCAPI'">
                <!-- OWS OGC url -->
                <x-input
                    :text="labelOgcUrl"
                    v-model="record.uri_OGC"
                    @input="$_onInputOgcUri"
                    id="ows-ogcuri"
                    name="ows-ogcuri"
                    :invalidMessage="uriOgcMessage"
                    :state="!noUriDefined && !uriOgcInvalidWords"
                    :placeholder="placeholders.OgcUri"
                    :maxlength="200"
                    :required="false"
                    :readonly="record.readonly"
                >
                </x-input>
            </div>
            <!-- OWS authentication -->
            <div v-if="OwsType === 'WMSWFS'">
                <b-form-group>
                    <b-form-group
                        :label="labelBeveiliging"
                        label-for="ddAuthentication"
                    >
                        <b-form-select
                            id="ddAuthentication"
                            name="ddAuthentication"
                            v-show="authenticationTypes"
                            v-model="authenticationCode"
                            :options="authenticationTypes"
                            :required="true"
                            :disabled="record.readonly"
                        >
                            <template v-slot:first>
                                <option :value="null">
                                    {{ $t('selectBoxMakeChoice') }}
                                </option>
                            </template>
                        </b-form-select>
                    </b-form-group>
                    <b-card
                        title="HTTP Basic Authentication"
                        id="owsSecurityDetailHttpBasicAuth"
                        v-show="
                            authenticationCode ===
                            authenticationTypeConst.HTTP_BASIC_AUTHENTICATION
                        "
                    >
                        <b-card-body>
                            <username-password
                                :required="
                                    authenticationCode ===
                                    authenticationTypeConst.HTTP_BASIC_AUTHENTICATION
                                "
                                :username="record.httpAuthUsername"
                                :password="record.httpAuthPassword"
                                :user-name-readonly="record.readonly"
                                @inputchanged="$_onAuthPasswordChanged"
                                @inputnamechanged="$_onAuthUsernameChanged"
                                :pwIsSaved="isSaving"
                                :pwtogglevisible="showEyeIcon"
                            >
                            </username-password>
                        </b-card-body>
                    </b-card>
                </b-form-group>
                <b-form-group>
                    <x-checkbox
                        id="txtHasOws"
                        name="txtHasOws"
                        :text="$t('hasOWS')"
                        v-model="record.hasOws"
                        :disabled="record.readonly"
                    >
                    </x-checkbox>
                </b-form-group>
                <b-form-group>
                    <b-button
                        v-if="showCheckButton"
                        @click="$_checkOws()"
                        :title="
                            $t('dashboard.dashboardAlerts.checkOwsButtonTitle')
                        "
                    >
                        {{
                            $t('dashboard.dashboardAlerts.checkOwsButtonLabel')
                        }}</b-button
                    >
                </b-form-group>
            </div>
            <!-- OWS add / cancel buttons -->
            <submit-cancel-remove
                :is-saving="isSaving"
                :is-deleting="isDeleting"
                :mode="mode"
                @cancel="$_onCancel"
                @remove="$_onRemove"
                :is-btn-save-disabled="isBtnSaveDisabled"
                :is-btn-remove-disabled="isBtnRemoveDisabled"
            >
            </submit-cancel-remove>
        </b-form>
        <ows-check :launchModal="launchDetailContentModal" :id="owsCheckId">
        </ows-check>
    </b-container>
</template>

<script>
    import {
        nprogress,
        isValidUri,
        uriHasInvalidWords
    } from '../../services/utilities';
    import { AUTHENTICATION_TYPE, MODE_ADD, MODE_EDIT } from '../../constants';
    import XInput from '../../components/BaseComponents/x-input.vue';
    import XSelect from '../../components/BaseComponents/x-select.vue';
    import XCheckbox from '../../components/BaseComponents/x-checkbox.vue';
    import modalMixin from '../../components/Mixins/ModalMixin.vue';
    import UsernamePassword from '../../components/UsernamePassword.vue';
    import SubmitCancelRemove from '../../components/SubmitCancelRemove.vue';
    import refcodeMixin from '../../components/Mixins/Refcodes/refcodeMixin.vue';
    import OwsCheck from './OwsCheck.vue';
    import { debounce } from 'lodash';

    export default {
        name: 'OwsDetail',

        mixins: [modalMixin, refcodeMixin],

        components: {
            'x-input': XInput,
            'x-select': XSelect,
            'x-checkbox': XCheckbox,
            'username-password': UsernamePassword,
            'submit-cancel-remove': SubmitCancelRemove,
            'ows-check': OwsCheck
        },

        props: { id: { type: Number, required: false, default: -1 } },

        data: function () {
            return {
                // Constants
                authenticationTypeConst: AUTHENTICATION_TYPE,
                launchDetailContentModal: false,

                // OWS record
                record: {},
                // labels for input fields
                labelName: this.$t('columnNameName'),
                labelWmsUrl: this.$t('columnWmsUriTitle'),
                labelWfsUrl: this.$t('columnWfsUriTitle'),
                labelWfsVersion: this.$t('columnWfsVersionTitle'),
                labelWmtsUrl: this.$t('columnWmtsUriTitle'),
                labelOgcUrl: this.$t('columnOgcUriTitle'),
                labelBeveiliging: this.$t('labelAuthentication'),
                // state variables
                isSaving: false,
                isDeleting: false,
                // add/edit mode
                mode: 0,
                // page title
                title: '',
                // validation
                errorInUse: false,
                nameMessage: '',
                noUriDefined: false,
                uriWmsInvalidWords: false,
                uriWfsInvalidWords: false,
                uriWmtsInvalidWords: false,
                uriOgcInvalidWords: false,
                uriWmsMessage: '',
                uriWfsMessage: '',
                uriWmtsMessage: '',
                uriOgcMessage: '',
                // list with authentication types
                authenticationTypes: [],
                // authentication code B/N
                authenticationCode: null,
                // placeholders list
                placeholders: {
                    name: this.$t('owsName'),
                    wmsUri: this.$t('owsWmsURL'),
                    wfsUri: this.$t('owsWfsURL'),
                    wmtsUri: this.$t('owsWmtsURL')
                },
                hasSavedPassword: false,
                owsCheckId: 0,
                OwsTypeOptions: [
                    { value: 'WMSWFS', text: 'WMS/WFS' },
                    {
                        value: 'OGCAPI',
                        text: 'OGC API'
                    }
                ],
                OwsType: 'WMSWFS'
            };
        },
        computed: {
            showCheckButton() {
                return this.mode === MODE_EDIT;
            },
            /**
             * Check if OWS can be saved
             * @returns {boolean} indicating if OWS can be saved
             */
            isBtnSaveDisabled() {
                return this.errorInUse || this.isSaving || this.isDeleting;
            },

            isBtnRemoveDisabled() {
                return this.record.readonly;
            },

            showEyeIcon() {
                return (
                    this.mode === MODE_ADD ||
                    (this.mode === MODE_EDIT && this.hasSavedPassword) ||
                    this.record.passwordIsChanged === true
                );
            },

            showWfsVersionSelector() {
                return this.record.getCapabilitiesWfsVersion > 1.99;
            },

            getWfsVersion() {
                if (this.record.sourceWfsVersion < 1.01) {
                    return '1.0.0';
                } else if (this.record.sourceWfsVersion < 1.11) {
                    return '1.1.0';
                }
                return '2.0.0';
            },

            getWfsVersionOptions() {
                return ['1.1.0', '2.0.0'];
            },
            isWmsWfs() {
                return !!(
                    this.record.uri_WMS ||
                    this.record.uri_WFS ||
                    this.record.uri_WMTS
                );
            }
        },
        mounted() {
            if (this.id && this.id > 0) {
                this.mode = MODE_EDIT;
                this.title = this.$t('owsDetailEditTitle');
                // get existing ows data from the backend
                this.$_loadExistingRecord();
            } else {
                // new form
                this.mode = MODE_ADD;
                this.title = this.$t('owsDetailAddTitle');
                // Retrieve Authentication types
                this.$_initializeAuthentication();
                this.OwsType = 'WMSWFS';
            }
        },

        methods: {
            /**
             * Load the existing OWS record from the API
             * @return {void} [does not return a value]
             */
            async $_loadExistingRecord() {
                nprogress.start();
                // get existing ows data from the backend
                await this.$store
                    .dispatch('useApiData', {
                        name: 'GetOwsById',
                        parameters: { id: this.id }
                    })
                    .then((result) => {
                        this.record = Object.assign({}, result.data);
                        // Determine whether record has saved password
                        this.hasSavedPassword = this.$_hasPassword();
                        // Retrieve Authentication types
                        this.$_initializeAuthentication();
                        this.OwsType = this.isWmsWfs ? 'WMSWFS' : 'OGCAPI';
                        nprogress.done();
                    });
            },

            /**
             * check OWS existence
             */
            $_onInputName: debounce(async function (searchTerm) {
                const data = {
                    name: searchTerm,
                    owsId: this.id ? this.id : 0
                };
                await this.$store
                    .dispatch('useApiData', {
                        name: 'GetOwsNameExists',
                        data
                    })
                    .then((result) => {
                        this.errorInUse = result.data;
                        if (this.errorInUse) {
                            this.nameMessage = this.$t('owsInUse');
                        }
                    });
            }, 750),

            /**
             * Update uri_WMS
             * @param  {string} newValue [new URI value]
             */
            $_onInputWmsUri(newValue) {
                this.record.uri_WMS = newValue.trim();
                this.$_hasUri();
            },

            /**
             * Update uri_WFS
             * @param  {string} newValue [new URI value]
             */
            $_onInputWfsUri(newValue) {
                this.record.uri_WFS = newValue.trim();
                this.$_hasUri();
            },

            $_onInputWfsVersion(newValue) {
                if (newValue === '1.0.0') {
                    this.record.sourceWfsVersion = 1.0;
                } else if (newValue === '1.1.0') {
                    this.record.sourceWfsVersion = 1.1;
                } else if (newValue === '2.0.0') {
                    this.record.sourceWfsVersion = 2.0;
                }
            },

            /**
             * Update uri_WMTS
             * @param  {string} newValue [new URI value]
             */
            $_onInputWmtsUri(newValue) {
                this.record.uri_WMTS = newValue.trim();
                this.$_hasUri();
            },
            $_onInputOgcUri(newValue) {
                this.record.uri_OGC = newValue.trim();
                this.$_hasUri();
            },

            $_onAuthPasswordChanged(password) {
                this.record.httpAuthPassword = password;
                this.record.passwordIsChanged = true;
            },

            $_onAuthUsernameChanged(username) {
                this.record.httpAuthUsername = username;
            },

            /**
             * Get the list with authentication types from nedglobe
             */
            $_initializeAuthentication() {
                this.authenticationTypes = [];
                // Retrieve Authentication types
                this.getRefCodes(AUTHENTICATION_TYPE.KEY).then((result) => {
                    for (const authenticationType of result.data) {
                        this.authenticationTypes.push({
                            value: authenticationType.code,
                            text: authenticationType.description
                        });
                    }
                    this.authenticationCode = this.record.httpAuthValue || null;
                    // reset password presentation
                    this.$emit('password-toggle-reset', [
                        this.record.httpAuthPassword
                    ]);
                });
            },

            /**
             * Goto the overview form
             */
            $_gotoOverview() {
                this.$router.push({ path: '/dashboard/ows' });
            },

            $_hasPassword() {
                return (
                    this.record.httpAuthPassword &&
                    this.record.httpAuthPassword.length === 0
                );
            },

            $_validateUriStrings() {
                this.uriWmsInvalidWords = false;
                this.uriWfsInvalidWords = false;
                this.uriWmtsInvalidWords = false;
                this.uriOgcInvalidWords = false;

                this.uriWmsMessage = '';
                this.uriWfsMessage = '';
                this.uriWmtsMessage = '';
                this.uriOgcMessage = '';

                if (uriHasInvalidWords(this.record.uri_WMS)) {
                    this.uriWmsMessage = this.$t('errorUrlInvalidWords');
                    this.uriWmsInvalidWords = true;
                }
                if (uriHasInvalidWords(this.record.uri_WFS)) {
                    this.uriWfsMessage = this.$t('errorUrlInvalidWords');
                    this.uriWfsInvalidWords = true;
                }
                if (uriHasInvalidWords(this.record.uri_WMTS)) {
                    this.uriWmtsMessage = this.$t('errorUrlInvalidWords');
                    this.uriWmtsInvalidWords = true;
                }
                if (uriHasInvalidWords(this.record.uri_OGC)) {
                    this.uriOgcMessage = this.$t('errorUrlInvalidWords');
                    this.uriOgcInvalidWords = true;
                }
                return (
                    !this.uriWmsInvalidWords &&
                    !this.uriWfsInvalidWords &&
                    !this.uriWmtsInvalidWords &&
                    !this.uriOgcInvalidWords
                );
            },

            async $_getOgcApiTileUrl(url) {
                const result = await this.$store.dispatch('useApiData', {
                    name: 'GetTileUrl',
                    data: { url: url }
                });
                return result.data;
            },

            /**
             * Check if any of the uri's is defined.
             * The uri has to start with 'http' as part of 'http://' or 'https://'
             * @return {bool} true if there is at least one uri, false otherwise
             */
            $_hasUri(options) {
                this.noUriDefined = false;

                if (this.record.uri_WMS) {
                    return true;
                }
                if (this.record.uri_WFS) {
                    return true;
                }
                if (this.record.uri_WMTS) {
                    return true;
                }

                if (this.record.uri_OGC) {
                    return true;
                }
                if (options && options.onSubmit) {
                    this.noUriDefined = true;
                }
                return false;
            },

            /**
             * Check if all defined uri's are valid.
             * @return {bool} true if all uri's are valid, false otherwise
             */
            $_hasValidUris(options) {
                const invalidUris = [];
                if (this.record.uri_WMS && !isValidUri(this.record.uri_WMS)) {
                    invalidUris.push('WMS');
                }
                if (
                    this.record.uri_WFS &&
                    !this.record.uri_WFS.includes('localhost') &&
                    !isValidUri(this.record.uri_WFS)
                ) {
                    invalidUris.push('WFS');
                }
                if (this.record.uri_WMTS && !isValidUri(this.record.uri_WMTS)) {
                    invalidUris.push('WMTS');
                }
                if (this.record.uri_OGC && !isValidUri(this.record.uri_OGC)) {
                    invalidUris.push('OGC');
                }
                return invalidUris;
            },

            /**
             * Check if any of the uri's is a http (and not https)
             * @return {bool} true if any of the uris is a http, false otherwise
             */
            $_hasHttpUri() {
                if (
                    this.record.uri_WMS &&
                    !this.record.uri_WMS.startsWith('https://')
                ) {
                    return true;
                }
                if (
                    this.record.uri_WFS &&
                    !this.record.uri_WFS.startsWith('https://')
                ) {
                    return true;
                }
                return (
                    this.record.uri_WMTS &&
                    !this.record.uri_WMTS.startsWith('https://')
                );
            },

            /**
             * Check if at minimum one Uri is defined.
             * Check validity of defined WMS/WFS.WMTS Uris
             * @return {bool} if Uris are valid, false otherwise
             */
            $_checkUris() {
                // check if one Uri is defined
                if (this.$_hasUri({ onSubmit: true })) {
                    // check validity of defined Uris
                    const invalidUris = this.$_hasValidUris();
                    if (invalidUris.length === 0) {
                        return true;
                    } else {
                        this.showToast({
                            title: this.$t('invalidUriTitle'),
                            message: this.$t('invalidUriMessage', [
                                invalidUris.join('/')
                            ]),
                            variant: 'danger',
                            autoHideDelay: 5000
                        });
                    }
                } else {
                    this.showToast({
                        title: this.$t('noUriDefinedTitle'),
                        message: this.$t('noUriDefinedMessage'),
                        variant: 'danger',
                        autoHideDelay: 5000
                    });
                }
                return false;
            },

            /**
             * Submit form data to the API
             * @return {Void}
             */
            async $_onSubmit() {
                if (!this.$refs.owsForm.checkValidity()) {
                    return;
                }

                if (this.errorInUse) {
                    return;
                }

                if (!this.$_validateUriStrings()) {
                    return;
                }

                // check if any of the 3 uri's has a value
                if (!this.$_checkUris()) {
                    return;
                }

                if (this.OwsType === 'OGCAPI') {
                    // the OGC API must be able to provide a TileUrl
                    // If we do not get a Tile Url then the OGCAPI url is considered
                    // to be invalid.
                    try {
                        const tileUrl = await this.$_getOgcApiTileUrl(
                            this.record.uri_OGC
                        );
                        if (!tileUrl) {
                            this.showToast({
                                title: this.$t('errorApiNedGlobe'),
                                message: this.$t('errorOgcApiNoTileUri'),
                                variant: 'danger',
                                autoHideDelay: 5000
                            });

                            return;
                        }
                    } catch (error) {
                        this.showToast({
                            title: this.$t('errorApiNedGlobe'),
                            message: this.$t('errorOgcApiNoTileUri'),
                            variant: 'danger',
                            autoHideDelay: 5000
                        });

                        return;
                    }
                }

                // check if any of the uri's has a http address
                if (this.$_hasHttpUri()) {
                    this.msgBoxConfirm({
                        okTitle: this.$t('btnSaveLabel'),
                        title: this.$t('owsNoHttps'),
                        message: this.$t('owsHttpsAdvice')
                    }).then((confirm) => {
                        if (confirm) {
                            this.$_onSave();
                        }
                    });
                } else {
                    this.$_onSave();
                }
            },

            /**
             * Save current changes to the OWS
             */
            async $_onSave() {
                this.record.httpAuthValue = this.authenticationCode;
                if (
                    this.record.httpAuthValue !==
                    AUTHENTICATION_TYPE.HTTP_BASIC_AUTHENTICATION
                ) {
                    this.record.httpAuthUsername = '';
                    this.record.httpAuthPassword = '';
                }
                nprogress.start();
                this.isSaving = true;
                await this.$store
                    .dispatch('useApiData', {
                        name: 'SaveOws',
                        data: this.record
                    })
                    .then(
                        (result) => {
                            this.isSaving = false;
                            this.showToast({
                                message: this.$t('owsCardSavedConfirmation'),
                                variant: 'success',
                                autoHideDelay: 2500,
                                noCloseButton: true
                            });
                            nprogress.done();
                            // if new OWS, goto overview
                            if (this.mode === MODE_ADD) {
                                this.$_gotoOverview();
                            } else {
                                // reload record
                                this.$_loadExistingRecord();
                            }
                        },
                        (error) => {
                            nprogress.done();
                            this.isSaving = false;
                            this.hasSavedPassword = this.$_hasPassword();
                            // get message text
                            var message = this.$t(error.message, error.parameters);
                            this.showToast({
                                title: this.$t('saveFailureTitle'),
                                message: message,
                                variant: 'danger',
                                autoHideDelay: 10000
                            });
                            // reset password presentation
                            if (this.record.httpAuthPassword) {
                                this.$emit('password-toggle-reset', [
                                    this.record.httpAuthPassword
                                ]);
                            }
                        }
                    );
            },

            /**
             * Cancel current modificatiosn to the OWS
             * and go back to the overview
             */
            $_onCancel() {
                this.$_gotoOverview();
            },

            /**
             * Remove current OWS
             * and go back to the overview
             */
            $_onRemove() {
                const data = { message: this.$t('owsCardDeletionsConfirmation') };
                this.msgBoxConfirm(data).then(async (confirmed) => {
                    if (confirmed) {
                        await this.$store
                            .dispatch('useApiData', {
                                name: 'DeleteOws',
                                parameters: { id: this.record.id }
                            })
                            .then(
                                (result) => {
                                    this.$_gotoOverview();
                                },
                                (error) => {
                                    this.showToast({
                                        title: this.$t('deleteFailureTitle'),
                                        message: this.$t('deleteFailureMessage', [
                                            `${error.response.data.message}`
                                        ]),
                                        variant: 'danger',
                                        autoHideDelay: 10000
                                    });
                                }
                            );
                    }
                });
            },
            $_checkOws() {
                this.launchDetailContentModal = !this.launchDetailContentModal;
                this.owsCheckId = this.id;
            }
        }
    };
</script>

<style scoped>
.error {
    color: red;
}
</style>
